/**
 * Radical positioning logic for arrows
 *
 * Handles calculations for arrows starting from radicals (unpaired electrons)
 */

import { v3 } from "@alchemiesolns/maths";
import { RADICAL_CONSTANTS } from "../core/constants";
import type { Point } from "../types";
import type { FormatOptions } from "@alchemiesolns/svg-format";

/**
 * Configuration for radical positioning
 */
export interface RadicalPositioningConfig {
  /**
   * Scalar multiplier for perpendicular offset
   * Default: 2/3
   */
  perpendicularOffsetScalar?: number;
}

/**
 * Manages radical positioning for arrow calculations
 */
export class RadicalPositioner {
  private config: Required<RadicalPositioningConfig>;

  constructor(config: RadicalPositioningConfig = {}) {
    this.config = {
      perpendicularOffsetScalar:
        config.perpendicularOffsetScalar ??
        RADICAL_CONSTANTS.PERPENDICULAR_OFFSET_SCALAR,
    };
  }

  /**
   * Calculate the starting point for an arrow from a radical
   *
   * @param radicalPoint - Radical point data
   * @param angledDir - Angled direction for offset
   * @param formatOpts - Formatting options
   * @returns Starting position for the arrow
   */
  public calculateStartPoint(
    radicalPoint: Point,
    angledDir: number[],
    formatOpts: FormatOptions
  ): number[] {
    const scalar = radicalPoint.offset;
    let pos = v3.addScaledVector(
      radicalPoint.position,
      radicalPoint.direction[0],
      scalar
    );
    pos = v3.addScaledVector(
      pos,
      angledDir,
      formatOpts.lonePair.radius * 2
    );
    return pos;
  }

  /**
   * Calculate the starting direction for an arrow from a radical
   *
   * @param radicalPoint - Radical point data
   * @param destPos - Destination position
   * @param formatOpts - Formatting options
   * @returns Direction vector
   */
  public calculateStartDirection(
    radicalPoint: Point,
    destPos: number[],
    formatOpts: FormatOptions
  ): number[] {
    const atomPos = radicalPoint.position;

    // Calculate initial direction based on radical orientation
    let dir = v3.normalize(
      v3.subVectors(
        v3.addScaledVector(
          atomPos,
          radicalPoint.direction[0],
          formatOpts.lonePair.offset
        ),
        atomPos
      )
    );

    // Add perpendicular offset based on destination position
    const parallelDir = v3.normalize(v3.subVectors(atomPos, destPos));
    let parDir = [0, -1, 0];
    if (parallelDir[1] < 0) {
      parDir = [0, 1, 0];
    }

    dir = v3.addVectors(
      dir,
      v3.scale(
        parDir,
        (formatOpts.atom.radius * 2) * this.config.perpendicularOffsetScalar
      )
    );

    return dir;
  }

  /**
   * Get current configuration
   */
  public getConfig(): Readonly<Required<RadicalPositioningConfig>> {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<RadicalPositioningConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
    };
  }
}

/**
 * Default singleton instance
 */
export const defaultRadicalPositioner = new RadicalPositioner();
